import { Injectable } from '@angular/core';
import { ActivityStudentAnswers, ActivityStudentAverage, AverageGrade, Exercise } from '@tekman/onmat/models';
import { OverviewTable } from '../models/overview-table.model';

@Injectable()
export class OverviewTableService {
  private overviewData: OverviewTable;
  private readonly COLUMNS = 10;

  private getLocale(): string {
    return window.location.hostname.includes('mx') ? 'es-MX' : 'es-ES';
  }

  initOverviewTable(
    exercises: Exercise[],
    studentAnswers: ActivityStudentAnswers[],
    studentAverages?: ActivityStudentAverage,
    groupAverageGrades?: AverageGrade[]
  ): OverviewTable {
    this.overviewData = { exercise: [], total: '', averageGrade: null, previous: false, next: false };
    let totalScore = 0;
    let totalInputs = 0;
    let isDelivered = false;
    exercises.forEach(exercise => {
      studentAnswers?.forEach(answer => {
        const average = studentAverages?.exercises.find(exercise => exercise.exerciseId === answer.exerciseId);
        answer = { ...answer, inputs: answer.inputs.filter(answer => answer.hasExpectedAnswer) };
        if (exercise.id === answer.exerciseId) {
          if (!exercise.isHidden) {
            totalInputs += average?.totalInputs;
          }
          answer.inputs.forEach(input => {
            if (Object.keys(input).includes('isCorrect')) {
              isDelivered = true;
            }
            if (input.isCorrect && !exercise.isHidden) {
              totalScore++;
              isDelivered = true;
            }
          });
          this.overviewData.exercise.push({
            id: exercise.id,
            order: exercise.order,
            score:
              exercise.isHidden || average?.totalInputs === 0
                ? '-'
                : !isDelivered
                ? `-/${average?.totalInputs}`
                : `${average?.correctInputs}/${average?.totalInputs}`,
            grade: average?.averageGrade?.toString() ?? '-',
            isEvaluable: !exercise.isHidden || exercise.isEvaluable,
            groupAverageGrade: groupAverageGrades?.find(grade => grade?.exerciseId === exercise.id)?.groupAverageGrade,
          });
        }
      });
    });
    const notAnswersExercise = exercises.filter(exercise => !studentAnswers?.some(answer => exercise.id === answer.exerciseId));
    notAnswersExercise.forEach(exercise => {
      this.overviewData.exercise.push({
        id: exercise.id,
        order: exercise.order,
        score: '-',
        grade: '-',
        isEvaluable: false,
      });
    });
    this.overviewData.exercise.sort((a, b) => {
      return a.order < b.order ? -1 : 1;
    });
    const evaluableExercise = this.overviewData.exercise.filter(exercise => exercise.isEvaluable && exercise.grade !== '-');
    const average =
      evaluableExercise.reduce((total, next) => {
        if (next.grade !== '-') {
          next = { ...next, grade: next.grade.replace(',', '.') };
          return total + Number(next.grade);
        } else {
          return total;
        }
      }, 0) / evaluableExercise.length;
    this.overviewData = {
      ...this.overviewData,
      total: !isDelivered ? `-/${totalInputs}` : `${totalScore}/${totalInputs}`,
      averageGrade: isDelivered && totalInputs > 0 ? average.toString() : '-',
      next: exercises.length > 10,
    };
    return { ...this.overviewData, exercise: this.overviewData.exercise.slice(0, 10) };
  }

  nextExercises(overviewData: OverviewTable): OverviewTable {
    const lastItem = overviewData.exercise[overviewData.exercise.length - 1].order + 1;
    const startIndex = this.overviewData.exercise[lastItem].order;
    const endIndex = startIndex + this.COLUMNS;
    const newExercises = this.overviewData.exercise.slice(startIndex, endIndex);
    return { ...this.overviewData, exercise: newExercises, previous: true, next: newExercises.length - overviewData.exercise.length >= 0 };
  }

  previousExercises(overviewData: OverviewTable): OverviewTable {
    const firstItem = overviewData.exercise[0].order - this.COLUMNS;
    const startIndex = this.overviewData.exercise[firstItem].order;
    const endIndex = startIndex + this.COLUMNS;
    return {
      ...this.overviewData,
      exercise: this.overviewData.exercise.slice(startIndex, endIndex),
      previous: startIndex >= this.COLUMNS,
      next: endIndex >= this.COLUMNS,
    };
  }
}
